import PropTypes from 'prop-types';
import classNames from 'classnames';
import Grid from '@mui/material/Grid';
import { connect } from 'react-redux';
import {
  SegmentTypeHelper,
  MediaFormatHelper,
  SpotTypeHelper,
  SpotSelectionStrategyHelper,
  Field2 as Field,
  VibeCheckbox,
  color,
} from 'vibeguide';
import CampaignValueAdjustments from './CampaignValueAdjustments';

function SegmentInformation({
  className = '',
  style = {},
  segmentType = '',
  mediaFormat = '',
  spotType = '',
  spotSelectionStrategy = '',
  frequencyCapped = false,
  frequencyCap = 0,
  allowBackToBackAdDelivery = false,
  allocateRemainder = false,
  onChangeCampaign = () => {},
  campaignValueAdjustments = [],
  editCampaignValueAdjustments = [],
  currencyCode = '',
  segmentStartDate = '',
  segmentEndDate = '',
  APISavingError = false,
  onRemoveRow = () => {},
  showCampaignValueAdjustments = true,
  isOpenExchange = false,
  user,
}) {
  const titleStyle = {
    fontSize: 16,
    fontWeight: 'bold',
    color: color.secondary,
  };

  // open-exchange segments are managed via location opt-in and have no configurable
  // delivery/type settings, so only the media format is relevant.
  if (isOpenExchange) {
    return (
      <div
        className={classNames('SegmentInformation', className)}
        style={style}
      >
        <Grid spacing={4} container>
          <Grid size={6}>
            <Field
              type="select"
              label="Media Format"
              name="mediaFormat"
              value={mediaFormat}
              options={MediaFormatHelper.list({ includeEmpty: true })}
              testId="order-segment-info-media-format-select"
              disabled
            />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div
      className={classNames('SegmentInformation', className)}
      style={style}
    >
      <Grid spacing={4} container>
        {/* Type */}
        <Grid size={6}>
          <Grid spacing={2} container>
            <Grid size={12}>
              <div
                style={titleStyle}
              >
                Type
              </div>
            </Grid>

            <Grid size={6}>
              <Field
                type="select"
                label="Segment Type"
                name="segmentType"
                value={segmentType}
                options={SegmentTypeHelper.list({ includeEmpty: true })}
                testId="order-segment-info-type-select"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="select"
                label="Media Format"
                name="mediaFormat"
                value={mediaFormat}
                options={MediaFormatHelper.list({ includeEmpty: true })}
                testId="order-segment-info-media-format-select"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="select"
                label="Spot Type"
                name="spotType"
                value={spotType}
                options={SpotTypeHelper.list({ includeEmpty: true })}
                testId="order-segment-info-spot-type-select"
                disabled
              />
            </Grid>
          </Grid>

          {showCampaignValueAdjustments && user.can('order_segment.view_campaign_value_adjustments') && (
            <CampaignValueAdjustments
              onChangeCampaign={onChangeCampaign}
              campaignValueAdjustments={campaignValueAdjustments}
              editCampaignValueAdjustments={editCampaignValueAdjustments}
              currencyCode={currencyCode}
              segmentStartDate={segmentStartDate}
              segmentEndDate={segmentEndDate}
              APISavingError={APISavingError}
              onRemoveRow={onRemoveRow}
            />
          )}
        </Grid>

        {/* Delivery */}
        <Grid size={6}>
          <Grid spacing={2} container>
            <Grid size={12}>
              <div
                style={titleStyle}
              >
                Delivery
              </div>
            </Grid>

            <Grid size={6}>
              <Field
                type="select"
                label="Spot Selection Strategy"
                name="spotSelectionStrategy"
                value={spotSelectionStrategy}
                options={SpotSelectionStrategyHelper.list({ includeEmpty: true })}
                testId="order-segment-info-spot-selection-strategy-select"
                disabled
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="toggle"
                label="Allocate Remainder"
                toggleProps={{
                  label: 'Allocate Remainder',
                }}
                value={allocateRemainder
                  ? 'on'
                  : 'off'}
                testId="order-segment-info-allocate-remainder-toggle"
                disabled
              />
            </Grid>

            {spotSelectionStrategy !== 'spots-per-hour' && (
              <>
                <Grid size={6}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <VibeCheckbox
                      rootStyle={{
                        padding: 0,
                        paddingRight: 8,
                      }}
                      label="Add Frequency Capping"
                      size={24}
                      color={color.primary}
                      name="frequencyCapped"
                      checked={frequencyCapped}
                      testId="order-segment-info-frequency-capped-checkbox"
                      disabled
                    />

                    <Field
                      type="number"
                      style={{
                        width: 52,
                      }}
                      name="frequencyCap"
                      value={frequencyCap}
                      testId="order-segment-info-frequency-cap-input"
                      disabled
                    />
                  </div>
                </Grid>

                <Grid size={6} />
              </>
            )}

            <Grid size={6}>
              <Field
                type="toggle"
                label="Ad Plays"
                toggleProps={{
                  label: 'Allow Duplicate Ads Back to Back',
                  helpText: 'Disabling limits inventory to ad programs',
                }}
                value={allowBackToBackAdDelivery
                  ? 'on'
                  : 'off'}
                testId="order-segment-info-ad-plays-toggle"
                disabled
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

SegmentInformation.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  segmentType: PropTypes.string,
  mediaFormat: PropTypes.string,
  spotType: PropTypes.string,
  spotSelectionStrategy: PropTypes.string,
  frequencyCapped: PropTypes.bool,
  frequencyCap: PropTypes.number,
  allowBackToBackAdDelivery: PropTypes.bool,
  allocateRemainder: PropTypes.bool,
  onChangeCampaign: PropTypes.func,
  campaignValueAdjustments: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    spend: PropTypes.number,
  })),
  editCampaignValueAdjustments: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    spend: PropTypes.number,
  })),
  currencyCode: PropTypes.string,
  segmentStartDate: PropTypes.string,
  segmentEndDate: PropTypes.string,
  APISavingError: PropTypes.bool,
  onRemoveRow: PropTypes.func,
  showCampaignValueAdjustments: PropTypes.bool,
  isOpenExchange: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps, null)(SegmentInformation);
